/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from '@mui/material/styles';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { FiMaximize2, FiX } from "react-icons/fi";
import defaultCarIcon from "assets/default_car_icon.png";
import notFoundImg from "assets/not_found.png";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiAvatar from "components/VuiAvatar";
import VuiBadge from "components/VuiBadge";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import CarDetailsModal from "./components/CarDetailsModal";
import CarEditModal from "./components/CarEditModal";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Create default icon instance
const defaultIcon = L.divIcon({
  className: 'car-marker-icon',
  html: `<img 
    src="${defaultCarIcon}" 
    style="
      width: auto;
      height: 40px;
      object-fit: contain;
    "
    alt="car"
  />`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20]
});

// Create a custom icon class for car markers
const createCarIcon = (iconUrl) => {
  if (!iconUrl) return defaultIcon;
  
  try {
    return L.divIcon({
      className: 'car-marker-icon',
      html: `<img 
        src="${iconUrl}" 
        style="
          width: auto;
          height: 40px;
          object-fit: contain;
        "
        alt="car"
      />`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20]
    });
  } catch (error) {
    console.error('Error creating car icon:', error);
    return defaultIcon;
  }
};

// Add this CSS to your component's style section
const carMarkerStyles = `
  .car-marker-icon {
    background: none;
    border: none;
  }
  .car-marker-icon img {
    width: auto !important;
    height: 40px !important;
    object-fit: contain;
  }
`;

function Cars() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("1"); // default to active
  const [showModal, setShowModal] = useState(false);
  const [carDetails, setCarDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCarForEdit, setSelectedCarForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [carToDelete, setCarToDelete] = useState(null);
  const { t, i18n } = useTranslation();

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.csc-taxi.tech';

  useEffect(() => {
    fetchCars();
    // eslint-disable-next-line
  }, [search, active]);

  const fetchCars = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }
      // Build query params
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('active', active); // always send active, either 1 or 0
      const url = `${API_URL}/api/admin/cars?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language,
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError("Oops, we haven't got JSON!");
      }
      const result = await response.json();
      if (result.status === 1) {
        // Ensure we're setting an array of cars
        setCars(Array.isArray(result.data) ? result.data : []);
      } else {
        throw new Error(result.message || 'Failed to fetch cars');
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
      setCars([]);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    } finally {
      setLoading(false);
    }
  };

  const openMenu = (event, car) => {
    setMenu(event.currentTarget);
    setSelectedCar(car);
  };

  const closeMenu = () => {
    setMenu(null);
    setSelectedCar(null);
  };

  const handleEdit = (car) => {
    setSelectedCarForEdit(car);
    setShowEditModal(true);
    closeMenu();
  };

  const handleDelete = (car) => {
    setCarToDelete(car);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/cars/${carToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      // Refresh the cars list
      fetchCars();
      setDeleteConfirmOpen(false);
      setCarToDelete(null);
    } catch (error) {
      console.error('Error deleting car:', error);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setCarToDelete(null);
  };

  const handleShowDetails = async (car) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/cars/${car.id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 1) {
        setCarDetails(result.data);
        setShowModal(true);
      } else {
        throw new Error(result.message || 'Failed to fetch car details');
      }
    } catch (error) {
      console.error('Error fetching car details:', error);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    }
    closeMenu();
  };

  const handleSaveCar = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/cars/${selectedCarForEdit.id}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 1) {
        // Refresh the cars list
        fetchCars();
      } else {
        throw new Error(result.message || 'Failed to update car');
      }
    } catch (error) {
      console.error('Error updating car:', error);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
      throw error; // Re-throw to let the modal handle the error
    }
  };

  const columns = [
    { name: "manufacturer", align: "left", label: t('car_table.manufacturer') },
    { name: "model", align: "left", label: t('car_table.model') },
    { name: "year", align: "center", label: t('car_table.year') },
    { name: "color", align: "center", label: t('car_table.color') },
    { name: "plate_number", align: "center", label: t('car_table.plate_number') },
    { name: "fuel_consumption", align: "center", label: t('car_table.fuel_consumption') },
    { name: "identification_marks", align: "left", label: t('car_table.identification_marks') },
    { name: "action", align: "center", label: t('car_table.action') },
  ];

  const rows = cars.map((car) => ({
    manufacturer: (
      <VuiBox display="flex" alignItems="center" px={1} py={0.5}>
        <VuiBox mr={2}>
          <VuiAvatar 
            src={car.images?.[0]} 
            alt={car.model} 
            size="sm" 
            variant="rounded" 
          />
        </VuiBox>
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {car.manufacturer}
        </VuiTypography>
      </VuiBox>
    ),
    model: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {car.model}
      </VuiTypography>
    ),
    year: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {car.year}
      </VuiTypography>
    ),
    color: (
      <VuiBox display="flex" alignItems="center" justifyContent="center">
        <Box sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: `#${car.color}`,
          border: '2px solid #fff',
          boxShadow: '0 0 2px #0002',
        }} />
      </VuiBox>
    ),
    plate_number: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {car.plate_number}
      </VuiTypography>
    ),
    fuel_consumption: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {car.fuel_consumption_per_100Km} {t('car_table.fuel_consumption_unit')}
      </VuiTypography>
    ),
    identification_marks: (
      <VuiBox sx={{ width: '180px', maxWidth: '180px', minWidth: '180px', p: 0 }}>
        <VuiTypography 
          variant="button" 
          color="text"
          sx={{
            width: '100%',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            fontSize: '15px',
            lineHeight: 1.3,
          }}
        >
          {car.identification_marks || t('car_table.none')}
        </VuiTypography>
      </VuiBox>
    ),
    action: (
      <VuiBox display="flex" flexDirection="row" gap={1}>
        <FaEye
          style={{ color: "#1976d2", cursor: "pointer" }}
          title={t('car_table.show_details')}
          onClick={() => handleShowDetails(car)}
        />
        <FaEdit
          style={{ color: "#ffa726", cursor: "pointer" }}
          title={t('car_table.edit')}
          onClick={() => handleEdit(car)}
        />
        <FaTrash
          style={{ color: "#e53935", cursor: "pointer" }}
          title={t('car_table.delete')}
          onClick={() => handleDelete(car)}
        />
      </VuiBox>
    ),
  }));

  return (
    <DashboardLayout>
      <style>{carMarkerStyles}</style>
      <DashboardNavbar />
      <VuiBox py={3} minHeight="100vh" display="flex" flexDirection="column" flex={1}>
        <VuiBox mb={3} flex={1}>
          {/* Car Table Card */}
          <Card sx={{ flex: 2, minWidth: 0, overflow: 'hidden', maxHeight: '458px' }}>
            {/* Search and filter controls */}
            <VuiBox display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }} mb={2} px={3}>
              <TextField
                variant="outlined"
                size="small"
                placeholder={t('car_table.search')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  style: { background: '#22223a', color: '#fff', borderRadius: 8 },
                }}
                sx={{ minWidth: 220 }}
              />
            
            </VuiBox>
            {loading ? (
              <VuiBox display="flex" justifyContent="center" p={3}>
                <VuiTypography variant="button" color="white">
                  {t('car_table.loading')}
                </VuiTypography>
              </VuiBox>
            ) : cars.length === 0 ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={5}>
                <img src={notFoundImg} alt="Not found" style={{ maxWidth: 180, opacity: 0.7, marginBottom: 16 }} />
                <VuiTypography variant="button" color="text">
                  {t('car_table.no_cars_found')}
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox
                sx={{
                  "& th": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[700]}`,
                    position: 'sticky',
                    top: 0,
                    background: '#191932',
                    zIndex: 2,
                  },
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                        `${borderWidth[1]} solid ${grey[700]}`,
                    },
                  },
                  width: '100%',
                  px: 3,
                }}
              >
                <Table columns={columns} rows={rows} />
              </VuiBox>
            )}
          </Card>
        </VuiBox>
      </VuiBox>
      <Footer />
      <CarDetailsModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setCarDetails(null);
        }}
        car={carDetails}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <CarEditModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCarForEdit(null);
        }}
        car={selectedCarForEdit}
        onSave={handleSaveCar}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            background: '#191932',
            color: '#fff',
            borderRadius: '18px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Are you sure you want to delete this car? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              background: '#d32f2f',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                background: '#b71c1c',
                color: '#ffffff',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Cars; 